import {
  TimelineEventStatus,
  TimelineEventStepStatus,
} from "components/ui/Timeline";
import dayjs from "dayjs";
import { ApiDashboard } from "utils/api/dashboard";
import { ApiUsersProfile } from "utils/api/users";
import { PartnerConstant } from "utils/partner";
import { formatCurrency } from "utils/string";

const getPayPeriodEndDescription = (status: TimelineEventStatus) => {
  if (status === TimelineEventStatus.past) return false;

  return "Your earnings will continue to count toward your spending limit until payday.";
};

const getPaydayDescription = ({
  paycheckReceived,
  status,
}: {
  paycheckReceived: boolean;
  status: TimelineEventStatus;
  bankName?: string;
}) => {
  if (status === TimelineEventStatus.past) return false;

  if (paycheckReceived) {
    return (
      <>
        <strong>You got paid!</strong> Your outstanding transactions were
        automatically repaid.
      </>
    );
  }

  if ([TimelineEventStatus.next, TimelineEventStatus.today].includes(status)) {
    return "The exact amount we receive from your payroll provider may still differ from the amount shown below.";
  }

  // biome-ignore lint/complexity/noUselessFragments: <No reason>
  return <>If payday were today&hellip;</>;
};

const getPaycheckStepStatus = ({
  paycheckReceived,
  status,
}: {
  paycheckReceived: boolean;
  status: TimelineEventStatus;
}) => {
  let stepStatus =
    status === TimelineEventStatus.today
      ? TimelineEventStepStatus.pending
      : TimelineEventStepStatus.incomplete;
  if (paycheckReceived) {
    stepStatus = TimelineEventStepStatus.complete;
  }
  return stepStatus;
};

const getPaycheckStepTitle = ({
  paycheckReceived,
  status,
}: {
  paycheckReceived: boolean;
  status: TimelineEventStatus;
}) => {
  let title = "Paycheck received";
  if (status === TimelineEventStatus.today && !paycheckReceived) {
    title = "Awaiting paycheck";
  }
  return title;
};

const getRepaymentStepStatus = (paycheckReceived: boolean) =>
  paycheckReceived
    ? TimelineEventStepStatus.complete
    : TimelineEventStepStatus.incomplete;

export const getEvents = ({
  dashboard,
  userProfile,
  partner,
}: {
  dashboard: ApiDashboard;
  userProfile: ApiUsersProfile;
  partner: PartnerConstant;
}) => {
  if (!dashboard) return [];

  const currentPayPeriodEnd = dayjs(dashboard.nextPayday)
    .subtract(7, "days")
    .format("YYYY-MM-DD");
  const currentPayday = dashboard.nextPayday;
  const paycheckAmount = dashboard.accruedNetEarnings;
  const repaymentAmount = dashboard.outstandingEwaTransactions;
  const remainderAmount = paycheckAmount - repaymentAmount;
  const savingsAmount = 200;
  const investmentAmount = 100;
  const paycheckReceived = false;
  const bankName = partner?.name;
  const employerName = userProfile?.payrollAccount?.employerName;

  const paydayEvent = {
    date: currentPayday,
    data: (status: TimelineEventStatus) => ({
      title: status === TimelineEventStatus.today ? "Payday 🎉" : "Payday",
      description: getPaydayDescription({
        paycheckReceived,
        status,
        bankName,
      }),
      steps: [
        {
          status: getPaycheckStepStatus({ paycheckReceived, status }),
          title: getPaycheckStepTitle({ paycheckReceived, status }),
          subtitle: `From ${employerName || "Payroll provider"}`,
          detail: `+$${formatCurrency(paycheckAmount)}`,
        },
        {
          status: getRepaymentStepStatus(paycheckReceived),
          title: "Paycheck routed",
          lineItems: [
            {
              label: "Advances repaid",
              amount: `–$${formatCurrency(repaymentAmount)}`,
            },
            {
              label: "Savings",
              amount: `–$${formatCurrency(savingsAmount)}`,
            },
            {
              label: "Investments",
              amount: `–$${formatCurrency(investmentAmount)}`,
            },
          ],
        },
        {
          status: getRepaymentStepStatus(paycheckReceived),
          title: "Remainder deposited",
          subtitle: "To checking account",
          detail: formatCurrency(
            remainderAmount - (savingsAmount + investmentAmount),
            true
          ),
        },
      ],
    }),
  };
  if (currentPayday === currentPayPeriodEnd) {
    return [paydayEvent];
  }
  const payPeriodEndEvent = {
    date: currentPayPeriodEnd,
    data: (status: TimelineEventStatus) => ({
      title: "End of Pay Period",
      description: getPayPeriodEndDescription(status),
    }),
  };
  return [payPeriodEndEvent, paydayEvent];
};
